import React from 'react';
import RichTextSlideRenderer, { InlineAudioRenderer } from './RichTextSlideRenderer';
import { formatInline } from '../utils/inlineMarkdown.js';

/** Card padding used by most Interactive exercise shells. */
export const EXERCISE_CARD_PAD = '1.5rem';

/** Instruction sits flush at the top of the exercise body (cancels card padding). */
export const INSTRUCTION_STYLE = {
    background: 'linear-gradient(90deg,#4361ee 0%,#7c3aed 100%)',
    color: 'white',
    borderRadius: 0,
    padding: '0.65rem 1.1rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    margin: `-${EXERCISE_CARD_PAD} -${EXERCISE_CARD_PAD} 1.25rem`,
    letterSpacing: '0.01em',
    whiteSpace: 'pre-line',
    lineHeight: 1.5,
};

/** Info-pinned: orange instruction strip so these exercises stand out. */
export const INSTRUCTION_STYLE_INFO = {
    ...INSTRUCTION_STYLE,
    background: 'linear-gradient(90deg,#f59e0b 0%,#ea580c 100%)',
};

/** Student-visible notes under instruction — no field label; indigo companion to the purple instruction banner. */
export const NOTES_UNDER_INSTRUCTION_STYLE = {
    background: 'linear-gradient(90deg, #e0e7ff 0%, #ede9fe 100%)',
    color: '#312e81',
    borderRadius: 0,
    padding: '0.65rem 1.1rem',
    fontWeight: 400,
    fontSize: '0.88rem',
    margin: `-1.25rem -${EXERCISE_CARD_PAD} 1.25rem`,
    borderBottom: '1px solid #c7d2fe',
    lineHeight: 1.55,
};

export const NOTES_UNDER_INSTRUCTION_STYLE_INFO = {
    ...NOTES_UNDER_INSTRUCTION_STYLE,
    background: 'linear-gradient(90deg, #ffedd5 0%, #fed7aa 100%)',
    color: '#9a3412',
    borderBottom: '1px solid #fdba74',
};

export function notesHasContent(html) {
    const trimmed = String(html || '')
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/<[^>]+>/g, '')
        .trim();
    return trimmed.length > 0;
}

export const instructionStyleForPad = (pad, extra = {}, isInfo = false) => ({
    ...(isInfo ? INSTRUCTION_STYLE_INFO : INSTRUCTION_STYLE),
    margin: `-${pad} -${pad} 1.25rem`,
    ...extra,
});

export const notesStyleForPad = (pad, isInfo = false) => ({
    ...(isInfo ? NOTES_UNDER_INSTRUCTION_STYLE_INFO : NOTES_UNDER_INSTRUCTION_STYLE),
    margin: `-1.25rem -${pad} 1.25rem`,
});

function renderInstruction(str, unblurredMap = {}, prefix = 'ex') {
    if (!str) return '';
    const html = formatInline(str, { unblurredMap: unblurredMap || {}, prefix: prefix || 'ex' });
    return <InlineAudioRenderer html={html} inline />;
}

/** Instruction banner + optional unlabeled notes beneath it (shared across exercise types). */
export default function ExerciseLead({ exercise, style, notesStyle, unblurred }) {
    const isInfo = !!exercise?.is_info;
    const defaultInstr = isInfo ? INSTRUCTION_STYLE_INFO : INSTRUCTION_STYLE;
    const defaultNotes = isInfo ? NOTES_UNDER_INSTRUCTION_STYLE_INFO : NOTES_UNDER_INSTRUCTION_STYLE;
    const resolvedStyle = style || defaultInstr;
    const instruction = String(exercise?.instruction || '').trim();
    const notesHtml = exercise?.notes || '';
    const hasNotes = notesHasContent(notesHtml);
    if (!instruction && !hasNotes) return null;

    let finalNotesStyle = notesStyle || defaultNotes;
    if (!notesStyle && style && typeof style.margin === 'string') {
        const m = style.margin.match(/^-([^ ]+)\s+-([^ ]+)\s+/);
        if (m) {
            finalNotesStyle = { ...defaultNotes, margin: `-1.25rem -${m[2]} 1.25rem` };
        }
    }
    const notesOnlyStyle = {
        ...defaultNotes,
        margin: resolvedStyle?.margin || defaultNotes.margin,
    };
    const notesColor = isInfo ? '#9a3412' : '#312e81';

    return (
        <>
            {instruction ? (
                <div className={`exercise-instruction-banner${isInfo ? ' exercise-instruction-banner--info' : ''}`} style={resolvedStyle}>
                    {renderInstruction(exercise.instruction, unblurred)}
                </div>
            ) : null}
            {hasNotes ? (
                <div
                    className={`exercise-notes-under-instruction${isInfo ? ' exercise-notes-under-instruction--info' : ''}`}
                    style={instruction ? finalNotesStyle : notesOnlyStyle}
                >
                    <RichTextSlideRenderer
                        content={notesHtml}
                        style={{ fontSize: '0.88rem', lineHeight: 1.55, color: notesColor, fontWeight: 400 }}
                    />
                </div>
            ) : null}
        </>
    );
}
